import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Chip,
  Avatar,
  useTheme,
  Box,
} from "@mui/material";
import {
  Mail,
  CheckCircle,
  HourglassTop,
  Lock,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store";
import { motion } from "framer-motion";

const statusConfig = {
  PENDING: {
    icon: HourglassTop,
    color: "warning",
    title: "Awaiting Approval",
    message:
      "Your account is currently under review. This process typically takes 1-2 business days.",
  },
  APPROVED: {
    icon: CheckCircle,
    color: "success",
    title: "Account Approved!",
    message:
      "Your account has been successfully verified. You can now access all platform features.",
  },
  REJECTED: {
    icon: Lock,
    color: "error",
    title: "Account Not Approved",
    message:
      "Unfortunately we couldn't approve your account at this time. Please see details below.",
  },
};

const ApprovalScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userDetails = useAppSelector(
    (state) => state.appLayout.userDetails
  ) as any;

  const approvalStatus =
    userDetails?.userType === "Merchant"
      ? userDetails?.merchantApprovalStatus
      : userDetails?.hostApprovalStatus;

  const {
    icon: StatusIcon,
    color,
    title,
    message,
  } = statusConfig[approvalStatus as keyof typeof statusConfig] ||
  statusConfig.PENDING;

  return (
    <Dialog open={true} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Avatar
            sx={{
              bgcolor: `black`,
              width: 80,
              height: 80,
              mx: "auto",
              mb: 2,
            }}
          >
            <StatusIcon
              sx={{
                fontSize: 40,
                color: "grey",
              }}
            />
          </Avatar>
        </motion.div>
        <Typography variant="h5" fontWeight={600}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ textAlign: "center", px: 4 }}>
        <Typography variant="body1" color="text.secondary" mb={2}>
          {message}
        </Typography>

        {(approvalStatus === "REJECTED" || approvalStatus === "RESUBMIT") && (
          <Box
            sx={{
              bgcolor: `${theme.palette.error.light}10`,
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant="caption" color="error">
              Reason:{" "}
              {userDetails?.adminComment ||
                "someone from team will contact you shortly."}
            </Typography>
          </Box>
        )}

        <Chip
          label={`Status: ${approvalStatus}`}
          color={color as any}
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />

        {approvalStatus === "PENDING" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <motion.span
              animate={{ opacity: [0.2, 1, 0.2] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <HourglassTop
                sx={{
                  fontSize: 16,
                  mr: 1,
                  color: theme.palette.warning.main,
                }}
              />
            </motion.span>
            <Typography variant="caption" color="text.disabled">
            {userDetails?.adminComment ?? "Processing your request..." }
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          pb: 4,
          flexDirection: approvalStatus === "REJECTED" ? "column" : "row",
          gap: 2,
        }}
      >
        {approvalStatus === "APPROVED" ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={<Mail />}
              onClick={() => {
                window.location.href = `mailto:support@kozyclan.com?subject=${userDetails.userType}_Approval_Query`;
              }}
            >
              Contact Support
            </Button>
          </>
        )}

        {approvalStatus !== "APPROVED" && (
          <Button variant="text" onClick={() => navigate(-1)}>
            Return Back
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
export default ApprovalScreen;
